import { apiClient } from './client'
import type {
  EvaluationPreviewResponse,
  EvaluationSubmitRequest,
  EvaluationSubmitResponse,
  EvaluationSingleRequest,
  EvaluationResult,
  JobListResponse,
  JobStatusResponse,
  EvaluationResultsResponse,
  FiltersResponse,
} from '@/types/api'

// Evaluation API service
export const evaluationsApi = {
  // Preview articles matching filters
  async preview(params: {
    knowledge_base?: string
    categories?: string[]
    evaluation_status?: 'all' | 'not_evaluated' | 'evaluated'
    page?: number
    limit?: number
  }): Promise<EvaluationPreviewResponse> {
    const queryParams = new URLSearchParams()
    if (params.knowledge_base) queryParams.append('knowledge_base', params.knowledge_base)
    if (params.categories?.length) queryParams.append('categories', params.categories.join(','))
    if (params.evaluation_status) queryParams.append('evaluation_status', params.evaluation_status)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())

    const response = await apiClient.get(`/v1/evaluations/preview?${queryParams}`)
    return response.data
  },

  // Submit batch evaluation job
  async submit(request: EvaluationSubmitRequest): Promise<EvaluationSubmitResponse> {
    const response = await apiClient.post('/v1/evaluations/submit', request)
    return response.data
  },

  // Evaluate single article (synchronous)
  async single(request: EvaluationSingleRequest): Promise<EvaluationResult> {
    const response = await apiClient.post('/v1/evaluations/single', request)
    return response.data
  },

  // List jobs
  async listJobs(params?: {
    page?: number
    limit?: number
    status?: string
  }): Promise<JobListResponse> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)

    const response = await apiClient.get(`/v1/evaluations/jobs?${queryParams}`)
    return response.data
  },

  // Get job status
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    const response = await apiClient.get(`/v1/evaluations/jobs/${jobId}`)
    return response.data
  },

  // Cancel job
  async cancelJob(jobId: string): Promise<{ message: string; job_id: string }> {
    const response = await apiClient.delete(`/v1/evaluations/jobs/${jobId}`)
    return response.data
  },

  // List results
  async listResults(params?: {
    page?: number
    limit?: number
    knowledge_base?: string
    flag?: string
    author_name?: string
    ownership_group_name?: string
  }): Promise<EvaluationResultsResponse> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.knowledge_base) queryParams.append('knowledge_base', params.knowledge_base)
    if (params?.flag) queryParams.append('flag', params.flag)
    if (params?.author_name) queryParams.append('author_name', params.author_name)
    if (params?.ownership_group_name) queryParams.append('ownership_group_name', params.ownership_group_name)

    const response = await apiClient.get(`/v1/evaluations/results?${queryParams}`)
    return response.data
  },

  // Search results by display_number or kb_number
  async searchResults(query: string): Promise<EvaluationResultsResponse> {
    const queryParams = new URLSearchParams()
    queryParams.append('q', query)
    const response = await apiClient.get(`/v1/evaluations/results/search?${queryParams}`)
    return response.data
  },

  // Get single result
  async getResult(displayNumber: string): Promise<{ result: EvaluationResult }> {
    const encoded = encodeURIComponent(displayNumber)
    const response = await apiClient.get(`/v1/evaluations/results/${encoded}`)
    return response.data
  },

  // Get filter options
  async getFilters(): Promise<FiltersResponse> {
    const response = await apiClient.get('/v1/evaluations/filters')
    return response.data
  },

  // Export results to Excel
  async exportToExcel(params?: {
    job_id?: string
    knowledge_base?: string
    flag?: string
  }): Promise<void> {
    const queryParams = new URLSearchParams()
    if (params?.job_id) queryParams.append('job_id', params.job_id)
    if (params?.knowledge_base) queryParams.append('knowledge_base', params.knowledge_base)
    if (params?.flag) queryParams.append('flag', params.flag)

    const response = await apiClient.get(`/v1/evaluations/results/export/excel?${queryParams}`, {
      responseType: 'blob',
    })

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    const filenameMatch = contentDisposition?.match(/filename=(.+)/)
    const filename = filenameMatch ? filenameMatch[1] : 'evaluation_results.xlsx'

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },
}

export default evaluationsApi
